/**
 * TabActionsMenu — the workspace tab ⋮ menu (#437).
 *
 * Exists as a component purely so `useFileActions` can be called: TabBar builds
 * its menu inside an IIFE, where a hook is illegal. It takes the tab-specific
 * items as `baseItems` and appends the shared access/sharing block.
 *
 * Fix round 1 (#437): stays MOUNTED across the tab menu closing, and `open`
 * (not the mount) controls whether the menu chrome renders. `useFileActions`
 * owns the shared items' dialog state; if this component unmounted whenever
 * the menu closed (as it did originally — TabBar rendered it only inside
 * `tabMenu.menu && …`), then clicking a shared item like "Who has access…"
 * would batch `tabMenu.close()` (→ `setMenu(null)`) together with the item's
 * own `setDialog(...)` in one React commit: the menu-gate would flip false,
 * this component would unmount in that SAME commit, and the pending dialog
 * state would be discarded before it ever painted. TabBar now keeps this
 * component mounted for as long as a tab has ever been targeted by the menu
 * (`menuTabTarget`, never cleared on close) and passes `open` purely to
 * toggle the `<FileContextMenu>` chrome — `{dialogs}` always renders,
 * regardless of `open`, so a dialog opened from a shared item survives the
 * menu closing around it.
 */
import { useMemo } from 'react'
import type { RefObject } from 'react'
import { FileContextMenu, type FileMenuItem } from '@papan/core-ui/ui'
import { useFileActions } from '@papan/collab/ui/use-file-actions'
import type { Tab } from './useWorkspace'

export function TabActionsMenu({
  open, x, y, tab, tenantId, baseItems, onClose, menuRef,
}: {
  /** Whether the menu chrome (`FileContextMenu`) should currently render.
   *  Independent of mount: this component itself stays mounted so its
   *  `useFileActions` dialog state survives `open` flipping to false. */
  open: boolean
  x: number
  y: number
  tab: Tab
  tenantId?: string
  baseItems: FileMenuItem[]
  onClose: () => void
  menuRef?: RefObject<HTMLDivElement | null>
}) {
  const { items, dialogs } = useFileActions({
    fileId: tab.fileId,
    name: tab.title,
    path: tab.path,
    isFolder: false, // a tab always holds a file
    tenantId,
    surface: 'tab',
  })

  // Shared items only open dialogs — they never close the menu themselves the
  // way baseItems' onClicks already do (each calls tabMenu.close() inline).
  // Close it here first, matching AppWindow's identical pattern for the same
  // catalog (setMenuOpen(false) before item.onClick()) — safe now that
  // closing the menu no longer unmounts the dialog along with it.
  const sharedItems = useMemo<FileMenuItem[]>(
    () => items.map(item => (item.kind === 'item' ? { ...item, onClick: () => { onClose(); item.onClick() } } : item)),
    [items, onClose],
  )

  // `useFileActions` returns the shared actions already organised into sections
  // (a "Share" header + a write-only "Develop" header), so this just appends
  // them after the tab-specific items behind one separator.
  const merged = useMemo<FileMenuItem[]>(
    () => (sharedItems.length ? [...baseItems, { kind: 'separator' as const }, ...sharedItems] : baseItems),
    [baseItems, sharedItems],
  )

  return (
    <>
      {open && <FileContextMenu x={x} y={y} items={merged} menuRef={menuRef} />}
      {dialogs}
    </>
  )
}
